import { ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'

import { useCallback, useEffect, useState } from 'react'

import { Grid, Pagination, css, styled } from '@mui/material'
import useMount from 'react-use/esm/useMount'
import { match } from 'ts-pattern'

import FollowItem from './follow-item'

import { StyledPaper } from '~/core/components/layout'
import { Title } from '~/core/components/title'
import { asMissEvanPagination } from '~/core/pagination'
import { CENTER_MIDDLE_TITLE } from '~/features/center/constants'
import type { FollowListParams } from '~/features/center/follow/types'
import { DataBlocksTitle, GuildTitle } from '~/features/guild/components/title'
import { CenterApi } from '~/services/fm/center'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'

const StyledPaperContainer = styled(StyledPaper)(
  ({ theme }) => css`
    background-color: ${theme.palette.background.default};
  `
)

const StyledPaginationContainer = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`
const FormattedMgirlNotFound = styled(MgirlNotice)`
  padding-top: 100px;
`

export const FollowList = () => {
  const [searchParams, setSearchParams] = useAppSearchParam()
  const [queryParams, setQueryParams] = useState<FollowListParams>({
    p: parseInt(searchParams.get('page') || '1'),
  })
  const { data: followListInfo, isFetching, isError } = CenterApi.getFollowList.useQuery(queryParams)
  const pagination = followListInfo?.pagination && asMissEvanPagination(followListInfo.pagination)

  const handleSetSearchParams = useCallback(
    (params: Partial<FollowListParams>) => {
      setSearchParams(new URLSearchParams({ page: params?.p?.toString() || '1' }))
    },
    [setSearchParams]
  )
  const handlePageChange = useCallback(
    (params: Partial<FollowListParams>) => {
      handleSetSearchParams(params)
      setQueryParams({ ...queryParams, ...params })
    },
    [queryParams, handleSetSearchParams]
  )

  // 如果查询的 page 超过了当前数据最大页数，则 page 设置为 1 并且重新请求接口
  useEffect(() => {
    if ((pagination?.page || 1) > (pagination?.page_count || 1)) {
      handlePageChange({ p: 1 })
    }
  }, [pagination, handlePageChange])

  useMount(() => {
    handleSetSearchParams({ p: queryParams.p })
  })

  return (
    <StyledPaperContainer elevation={0}>
      <Title>{['我的关注', CENTER_MIDDLE_TITLE]}</Title>
      <DataBlocksTitle>
        <GuildTitle title="我的关注" />
      </DataBlocksTitle>
      {match(isFetching)
        .with(true, () => <Loading type={ELoadingType.Cat} />)
        .otherwise(() =>
          match(isError)
            .with(true, () => (
              <FormattedMgirlNotFound image={EMGirlType.NotFound} description="加载失败，请刷新重试~" />
            ))
            .otherwise(() =>
              followListInfo?.Datas.length ? (
                <>
                  <Grid container rowSpacing={1} columnSpacing={1} sx={{ marginTop: '-2px' }}>
                    {followListInfo?.Datas.map((card) => (
                      <Grid item xs={12} sm={12} md={6} lg={4} xl={4} key={card.room_id}>
                        <FollowItem card={card} />
                      </Grid>
                    ))}
                  </Grid>
                  <StyledPaginationContainer>
                    <Pagination
                      page={pagination?.page}
                      count={pagination?.page_count}
                      showFirstButton
                      showLastButton
                      onChange={(_, page) => handlePageChange({ p: page })}
                    />
                  </StyledPaginationContainer>
                </>
              ) : (
                <FormattedMgirlNotFound image={EMGirlType.NotFound} description="还没有关注直播间哦～" />
              )
            )
        )}
    </StyledPaperContainer>
  )
}
